// Public project quotation page (Session ABN, Item 3). No login: the covering email links the
// customer here (/project-quotation/:token), making the HTML page the PRIMARY customer-facing
// quotation format (mirroring the maintenance quote accept page); the PDF remains available
// via the Download button and stays the office-filed format.
//
// Renders ALL the content the PDF renders (the server sends the same allow-listed builder
// output): reference, title, site + client, existing installation, scope of works, pricing,
// payment stages, standards, BSR + Section 20 notes, the quotation terms, the full project
// works Terms and Conditions, and the ESA-pattern execution block (the Signable merge fields
// as they appear on the signing document). Deliberately NO Signable button: signing happens
// via the link in the covering email only (the locked acceptance route).
//
// Brand (prompt-prescribed): dark #050816 header, purple #7d21c7 accents, green #63C41F for
// positive actions, Rajdhani headings, Montserrat body -- the standard dark app tokens.
// House rules: plain English, no personal names, phone 0203 576 4760, pound symbol.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type {
  PublicProjectQuotationView,
  PublicProjectQuotationData,
  ProjectWorksTermsClause,
} from "../../lib/api";
import { FullScreenLoader } from "../../components/Loader";
import { SKYLINE_WATERMARK_COLOUR, SKYLINE_WATERMARK_OPACITY, SKYLINE_WATERMARK_HEIGHT } from "../../lib/skyline";

const PHONE = "0203 576 4760";

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ProjectQuotationPage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PublicProjectQuotationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | "error" | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.publicProjectQuotation
      .get(token)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((err) => {
        if (!live) return;
        setError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  if (loading) return <FullScreenLoader />;

  if (error || !view) {
    return (
      <Shell>
        <div className="rounded-ele border border-ele-purple-accent bg-black/20 p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">
            {error === "not_found" ? "We could not find this quotation" : "Something went wrong"}
          </h1>
          <p className="text-ele-body">
            {error === "not_found"
              ? "This link may be out of date. Please contact us and we will send you a fresh one."
              : "Please try again in a moment. If it keeps happening, call us and we will help."}
          </p>
          <p className="mt-3 text-ele-body">
            {PHONE} | info@eastlondonelevators.co.uk
          </p>
        </div>
      </Shell>
    );
  }

  const q = view.quotation;
  const existing = q.existingInstall ?? [];
  const vatPct = Math.round(q.vatRate * 100);
  const ex = q.contractValueExVat;
  const vat = ex != null ? ex * q.vatRate : null;
  const inc = ex != null && vat != null ? ex + vat : null;

  return (
    <Shell wide>
      {/* Header (Session ACE): dark band, brand PNG logo left, document title right. */}
      <header className="mb-6 flex flex-col items-center gap-4 border-b-2 border-ele-purple pb-5 text-center sm:flex-row sm:justify-between sm:text-left">
        <img src="/brand/ele-logo.png" alt="East London Elevators" className="h-12 w-auto" />
        <div className="sm:text-right">
          <p className="font-menu text-xs uppercase tracking-widest text-ele-green">Formal quotation</p>
          <h1 className="mt-1 font-heading text-3xl tracking-wide text-ele-white">{q.title}</h1>
          <p className="mt-1 text-sm text-ele-body">{q.typeLabel} | East London Elevators Ltd</p>
        </div>
      </header>

      {/* Actions: download the PDF version; share the page link. NO Signable button here --
          signing happens via the link in the covering email only. */}
      <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
        <a
          href={api.publicProjectQuotation.pdfHref(token)}
          download
          className="rounded-ele bg-ele-green px-5 py-2.5 font-menu text-xs font-bold uppercase tracking-wide text-ele-bg transition hover:brightness-110"
        >
          Download PDF
        </a>
        <ShareQuotationButton quoteRef={q.quoteRefDisplay ?? q.projectRef} />
      </div>

      {/* Meta row. */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Fact label="Quotation" value={q.quoteRefDisplay ?? q.projectRef} />
        <Fact label="Date" value={q.dateDisplay} />
        <Fact label="Lifts in scope" value={q.liftsInScope != null ? String(q.liftsInScope) : existing.length > 0 ? String(existing.length) : "-"} />
        <Fact label="Client ref" value={q.clientRef?.trim() || "-"} />
      </div>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-ele border border-ele-purple-accent bg-black/20 px-4 py-3">
          <SectionLabel>Site</SectionLabel>
          <p className="mt-0.5 text-ele-white">{q.siteName?.trim() || "-"}</p>
          {q.siteAddress ? <p className="text-sm text-ele-body">{q.siteAddress}</p> : null}
        </div>
        <div className="rounded-ele border border-ele-purple-accent bg-black/20 px-4 py-3">
          <SectionLabel>Prepared for</SectionLabel>
          <p className="mt-0.5 text-ele-white">{q.clientName?.trim() || "-"}</p>
        </div>
      </div>

      {q.liftDetails?.trim() ? (
        <Section title="Lift details">
          <Multiline text={q.liftDetails} />
        </Section>
      ) : null}

      {existing.length > 0 ? (
        <Section title="Existing installation">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {existing.map((r, i) => (
              <div key={i} className="rounded-ele border border-ele-purple-accent bg-black/20 p-3">
                <p className="font-heading text-sm uppercase tracking-wide text-ele-white">{r.liftRef || "Lift"}</p>
                <dl className="mt-2 space-y-1 text-sm">
                  <InstallFact k="Type" v={r.type} />
                  <InstallFact k="Controller" v={r.controller} />
                  <InstallFact k="Emergency comms" v={r.emergencyComms} />
                </dl>
              </div>
            ))}
          </div>
        </Section>
      ) : q.conditionNotes?.trim() ? (
        <Section title="Existing installation">
          <Multiline text={q.conditionNotes} />
        </Section>
      ) : null}

      {/* Same gate as the PDF: the supplier block renders whenever it is non-null (it is only
          non-null once at least one of its fields is set), so a lead-time-only or
          compliance-note-only block never silently drops from the page (Session AED). */}
      {q.supplier ? (
        <Section title="Components and supply">
          {q.supplier.name ? (
            <p className="text-ele-white">
              <span className="font-bold">Supplier:</span> {q.supplier.name}
            </p>
          ) : null}
          {q.supplier.supplies ? <p className="mt-1">{q.supplier.supplies}</p> : null}
          {q.supplier.leadTime ? (
            <p className="mt-1">
              <span className="font-bold text-ele-white">Lead time:</span> {q.supplier.leadTime}
            </p>
          ) : null}
          {q.supplier.complianceNote ? <p className="mt-2 text-sm opacity-80">{q.supplier.complianceNote}</p> : null}
        </Section>
      ) : null}

      {q.scopeSummary?.trim() ? (
        <Section title="Scope of works">
          <Multiline text={q.scopeSummary} />
        </Section>
      ) : null}

      {q.exclusions?.trim() ? (
        <Section title="Exclusions">
          <Multiline text={q.exclusions} />
        </Section>
      ) : null}

      <Section title="Programme">
        {q.startDate || q.targetCompletion ? (
          <>
            {q.startDate ? (
              <p>
                <span className="font-bold text-ele-white">Proposed start:</span> {q.startDate}
              </p>
            ) : null}
            {q.targetCompletion ? (
              <p>
                <span className="font-bold text-ele-white">Target completion:</span> {q.targetCompletion}
              </p>
            ) : null}
          </>
        ) : (
          <p>The programme will be confirmed on acceptance.</p>
        )}
      </Section>

      <Section title="Lift works pricing (ex. VAT)">
        <PricingTable q={q} />
        {ex != null ? (
          <table className="mt-4 w-full text-sm">
            <tbody>
              <tr className="border-b border-ele-purple-accent">
                <td className="py-1.5 font-bold text-ele-body">VAT @ {vatPct}%</td>
                <td className="py-1.5 text-right font-bold text-ele-white">{gbp(vat!)}</td>
              </tr>
              <tr className="border-b border-ele-purple-accent">
                <td className="py-1.5 font-bold text-ele-body">Total (Ex. VAT)</td>
                <td className="py-1.5 text-right font-bold text-ele-white">{gbp(ex)}</td>
              </tr>
              <tr>
                <td className="py-2 font-heading text-base uppercase tracking-wide text-ele-green">Total (Inc. VAT)</td>
                <td className="py-2 text-right font-heading text-base text-ele-green">{gbp(inc!)}</td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </Section>

      {q.stages.length > 0 ? (
        <Section title="Payment schedule">
          <table className="w-full text-sm">
            <tbody>
              {q.stages.map((s, i) => (
                <tr key={i} className="border-b border-ele-purple-accent">
                  <td className="py-1.5 text-ele-body">{s.label}</td>
                  <td className="py-1.5 text-right font-bold text-ele-white">{gbp(s.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-sm opacity-80">Each stage is invoiced as it falls due, monthly terms, 30 days. VAT is added to each stage at the prevailing rate.</p>
        </Section>
      ) : null}

      <Section title="Applicable standards">
        <p>{q.applicableStandard?.trim() || "The works are carried out in accordance with the current BS EN 81 series and all applicable regulations."}</p>
      </Section>

      {q.bsrRequired ? (
        <Note>
          <span className="font-bold text-ele-white">Higher-Risk Building.</span> These works are notifiable under the Building Safety Act. Works will not
          commence until the Building Safety Regulator's Gateway 2 approval is in place. We will manage the Gateway submission as part of the project.
        </Note>
      ) : null}
      {q.section20Required ? (
        <Note>
          <span className="font-bold text-ele-white">Section 20 consultation.</span> As the cost to an individual leaseholder exceeds the statutory
          threshold, a Section 20 leaseholder consultation under the Landlord and Tenant Act 1985 applies. The three statutory consultation stages, of at
          least 30 days each, will be completed before the works are awarded.
        </Note>
      ) : null}

      <Section title="Quotation terms">
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {view.quotationTerms.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </Section>

      {/* The full project works Terms and Conditions (after the pricing, before the execution
          block -- the prescribed placement). */}
      <Section title="Terms and Conditions">
        <h3 className="font-heading text-base uppercase tracking-wide text-ele-green">Key terms</h3>
        <dl className="mt-2 space-y-2">
          {view.terms.keyTerms.map((kt) => (
            <div key={kt.label}>
              <dt className="font-bold text-ele-white">{kt.label}</dt>
              <dd className="text-sm">{kt.text}</dd>
            </div>
          ))}
        </dl>

        <h3 className="mt-6 font-heading text-base uppercase tracking-wide text-ele-green">The Agreement</h3>
        {view.terms.agreementClauses.map((c) => (
          <Clause key={c.heading} clause={c} />
        ))}

        <h3 className="mt-6 font-heading text-base uppercase tracking-wide text-ele-green">Conditions of Contract</h3>
        {view.terms.conditions.map((c) => (
          <Clause key={c.heading} clause={c} />
        ))}
      </Section>

      {/* Execution block: the ESA-pattern signing rows with the Signable merge fields, exactly
          as they appear on the signing document. Deliberately informational -- the fillable
          fields live on the Signable envelope the covering email links to. */}
      {/* Session AED: an EXECUTED quotation stops inviting a signature (the maintenance accept
          page's "accepted" pattern) -- revisiting the link after signing shows the executed
          state, never a fresh invitation to sign. */}
      {view.signed ? (
        <Section title="Execution and acceptance">
          <div className="rounded-ele border-2 border-ele-green bg-black/20 p-4">
            <p className="font-heading text-lg text-ele-green">
              This quotation has been accepted and signed{view.signedOnDisplay ? ` on ${view.signedOnDisplay}` : ""}.
            </p>
            <p className="mt-2 text-sm">
              The signed record is held on file by East London Elevators; no further signature is needed. We will be in touch to schedule the works. For
              any queries, please contact us on {PHONE} or info@eastlondonelevators.co.uk.
            </p>
          </div>
        </Section>
      ) : (
        <Section title="Execution and acceptance">
          <p className="text-sm">
            To formally accept this quotation, please sign via the secure Signable link sent separately by East London Elevators. One signature executes the
            agreement, including the Conditions of Contract. The signature fields below appear on the signing document.
          </p>
          <div className="mt-3 rounded-ele border-2 border-ele-purple bg-black/20 p-4">
            <ExecField label="Signature" tag="{signature:signer1:Sign+Here}" tall />
            <ExecField label="Full name (print)" tag="{text:signer1:Print+Name}" />
            <ExecField label="Position" tag="{text:signer1:Position}" />
            <ExecField label="Date" tag="{date:signer1:Date}" />
          </div>
          <p className="mt-3 text-sm opacity-80">
            Works will be scheduled on receipt of signed acceptance and, where applicable, the deposit payment. For queries prior to acceptance, please
            contact East London Elevators on {PHONE} or info@eastlondonelevators.co.uk.
          </p>
        </Section>
      )}

      {/* Footer (Session ACE): purple rule, the skyline strip, then a dark footer bar with the logo
          left, company details centre and the phone right.
          Session AFW: the strip now uses the SHARED masked skyline (lib/skyline.ts), the same
          treatment as the Client Portal footer and the engineer PWA home. This SUPERSEDES the AFJ
          note that the public project quotation "carries its own, deliberately different" footer:
          the Director directed it match the polished near-white watermark. It was rendering the raw
          purple line art at opacity-45, which measured a contrast of ~1.11 against the #050816 page
          and read as a washed-out purple graphic. This is a public DARK page (bg-ele-bg, not
          themable), so it takes the DARK-surface values unconditionally. A masked <div> because a
          mask fills the element's background, not an <img>'s pixels. */}
      <footer className="mt-8 border-t border-ele-purple pt-4">
        <div
          aria-hidden="true"
          data-ele-skyline=""
          className="w-full"
          style={{
            height: SKYLINE_WATERMARK_HEIGHT,
            backgroundColor: SKYLINE_WATERMARK_COLOUR,
            opacity: SKYLINE_WATERMARK_OPACITY,
            // The 355 KB derivative the Client Portal footer masks, NOT the ~1 MB master: this is a
            // customer-facing page and the mask renders indistinguishably at the painted 240x80 size
            // (a CSS mask takes one URL, so there is no webp negotiation to keep). Session AFW review.
            WebkitMaskImage: "url('/brand/london-skyline-1200.png')",
            maskImage: "url('/brand/london-skyline-1200.png')",
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
        <div className="mt-4 flex flex-col items-center gap-3 rounded-ele bg-black/40 px-5 py-4 text-center text-xs text-ele-body sm:flex-row sm:justify-between">
          <img src="/brand/ele-logo.png" alt="" className="h-9 w-auto" />
          <div className="sm:text-center">
            <p>For and on behalf of East London Elevators.</p>
            <p className="mt-1">East London Elevators Ltd | Company No. 15429089 | VAT GB493339461</p>
            <p className="mt-1">Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
            <p className="mt-1">info@eastlondonelevators.co.uk</p>
          </div>
          <p className="font-heading text-base font-bold text-ele-loler">{PHONE}</p>
        </div>
      </footer>
    </Shell>
  );
}

function Shell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-dvh bg-ele-bg px-4 py-8 text-ele-body">
      <div className={`mx-auto w-full ${wide ? "max-w-3xl" : "max-w-md"}`}>{children}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="font-menu text-[10px] uppercase tracking-wider text-ele-green">{children}</p>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 border-b border-ele-purple pb-1 font-heading text-lg uppercase tracking-wide text-ele-white">{title}</h2>
      {children}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-black/20 px-3 py-2">
      <p className="font-menu text-[10px] uppercase tracking-wider text-ele-green">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-ele-white">{value}</p>
    </div>
  );
}

function InstallFact({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ele-body">{k}</dt>
      <dd className="text-right text-ele-white">{v?.trim() || "-"}</dd>
    </div>
  );
}

// Preserve the author's line breaks (the structured one-item-per-line scope of works must
// never collapse into a run-on paragraph -- same rule as the PDF template).
function Multiline({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);
  return (
    <p className="text-sm leading-relaxed">
      {lines.map((line, i) => (
        <span key={i}>
          {line}
          {i < lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </p>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <div className="mb-4 rounded-ele border border-ele-purple-accent bg-black/20 p-3 text-sm">{children}</div>;
}

function Clause({ clause }: { clause: ProjectWorksTermsClause }) {
  return (
    <div className="mt-3">
      <h4 className="font-heading text-sm uppercase tracking-wide text-ele-white">{clause.heading}</h4>
      {clause.paragraphs.map((p, i) => (
        <p key={i} className="mt-1 text-sm">
          {p}
        </p>
      ))}
    </div>
  );
}

// An execution-block signing row (the ESA pattern): the label above a bordered field carrying
// the Signable merge-field tag in the same visible light gray the signing PDF uses.
function ExecField({ label, tag, tall = false }: { label: string; tag: string; tall?: boolean }) {
  return (
    <div className="mb-3 last:mb-0">
      <p className="text-xs text-ele-body">{label}</p>
      <div className={`mt-1 rounded border border-ele-purple-accent bg-black/30 px-3 ${tall ? "py-5" : "py-2"}`}>
        <span className="font-body text-sm" style={{ color: "#AAAAAA" }}>
          {tag}
        </span>
      </div>
    </div>
  );
}

// The pricing table: line items when they exist, else the single synthesised works row from
// the contract value, else the price-separately note (the same three paths as the PDF).
function PricingTable({ q }: { q: PublicProjectQuotationData }) {
  const lineItems = q.lineItems ?? [];
  const ex = q.contractValueExVat;
  const worksElement = /\b(works|report)$/i.test(q.typeLabel.trim()) ? q.typeLabel.trim() : `${q.typeLabel.trim()} works`;
  const rows =
    lineItems.length > 0
      ? lineItems
      : ex != null
        ? [{ refLabel: "", element: worksElement, scopeDescription: "", notes: "", basis: "Fixed price", priceExVat: ex }]
        : [];
  if (rows.length === 0) return <p>The price for these works is provided separately and will be confirmed in writing.</p>;
  return (
    <div data-safetynet className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-black/40 text-left font-heading text-xs uppercase tracking-wide text-ele-white">
            <th className="px-3 py-2">Element</th>
            <th className="px-3 py-2">Scope description</th>
            <th className="px-3 py-2 text-center">Basis</th>
            <th className="px-3 py-2 text-right">Price (Ex. VAT)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((li, i) => (
            <tr key={i} className="border-b border-ele-purple-accent align-top">
              <td className="px-3 py-2">
                {li.refLabel ? <p className="font-menu text-[10px] font-bold uppercase tracking-wide text-ele-body">{li.refLabel}</p> : null}
                <p className="font-bold text-ele-white">{li.element || "-"}</p>
              </td>
              <td className="px-3 py-2">
                {li.scopeDescription}
                {li.notes ? <p className="mt-1 text-xs italic opacity-70">{li.notes}</p> : null}
              </td>
              <td className="px-3 py-2 text-center whitespace-nowrap">{li.basis}</td>
              <td className="px-3 py-2 text-right font-bold text-ele-white whitespace-nowrap">{gbp(li.priceExVat)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-black/40">
            <td colSpan={3} className="px-3 py-2 font-heading text-sm uppercase tracking-wide text-ele-white">
              Total (Ex. VAT)
            </td>
            <td className="px-3 py-2 text-right font-heading text-sm text-ele-white">{gbp(rows.reduce((s, r) => s + r.priceExVat, 0))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Share the quotation's link (the maintenance quote's proven pattern, Session AAG): the Web
// Share API where supported, else copy the current page URL to the clipboard with a brief
// "Link copied" confirmation. The URL is the persistent /project-quotation/:token page.
// Exported for direct unit testing.
export function ShareQuotationButton({ quoteRef }: { quoteRef: string }) {
  const [copied, setCopied] = useState(false);
  async function onShare() {
    const url = window.location.href;
    const title = `Project Quotation ${quoteRef} - East London Elevators`;
    const nav: Navigator | undefined = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav && typeof nav.share === "function") {
      try {
        await nav.share({ title, url });
      } catch {
        // The customer dismissed the native share sheet, or it failed; nothing to do.
      }
      return;
    }
    try {
      await nav?.clipboard?.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access blocked (rare, e.g. an insecure context); leave the label unchanged.
    }
  }
  return (
    <button
      type="button"
      onClick={onShare}
      className="inline-flex items-center gap-2 rounded-ele border border-ele-purple px-5 py-2.5 font-menu text-xs font-bold uppercase tracking-wide text-ele-white transition hover:bg-black/30"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
      </svg>
      {copied ? "Link copied" : "Share this quotation"}
    </button>
  );
}
